import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { Card, CardContent, Grid, Typography, useTheme } from '@mui/material';
import SignupAndLogin from '../SignupAndLogin';
import RedeemGiftCard from '../Cart/RedeemGiftCard';
import { redeemGiftCard } from 'src/services/cart-order/gift-cards';
import { useEffect, useState } from 'react';
import { centsToDollars, formatNumberWithCommas } from 'src/utils/currency';
import { AxiosError } from 'axios';
import logger from 'logger';
import useAnalytics from 'src/analytics/useAnalytics';
import { OrderCompletedEventProps } from 'src/analytics/interfaces';
import {
  GIFT_CARD_FAQ,
  SEGMENT_EVENT_GIFT_CARD,
} from 'src/constants/gift-cards';
import FaqAccordion from '../FAQAccordion/FaqAccordion';
import LayoutV2 from '../LayoutV2';
import Header from '../LayoutV2/header';
import Back from '../LayoutV2/header/back';
import Logo from '../LayoutV2/header/logo';
import AccountMenu from '../LayoutV2/account-menu';
import Footer from '../Footer';
import { useRouter } from 'next/router';

const GiftCardRedeemComponent = () => {
  const theme = useTheme();
  const router = useRouter();
  const { track } = useAnalytics();
  const user = useNonNullContext(UserContext);
  const [redeemGiftCardError, setRedeemGiftCardError] = useState<string | null>(
    null,
  );
  const [redeemedCreditInCents, setRedeemedCreditInCents] = useState<
    number | null
  >(null);
  const [creditBalanceInCents, setCreditBalanceInCents] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (user.user?.creditInCents) {
      setCreditBalanceInCents(user.user.creditInCents);
    }
  }, []);

  const redeemGiftCardHandler = async (code: string) => {
    try {
      const res = await redeemGiftCard({ code });
      setRedeemedCreditInCents(res.amountRedeemedInCents);
      setCreditBalanceInCents(res.creditAfterRedemptionInCents);
      if (user.user) {
        try {
          await track<OrderCompletedEventProps>('Gift Card Redeemed', {
            //when a gc is purchased, we add the stripe checkout session id as order_id.
            // In here we don't have the gc id, so we use the code as order_id.
            order_id: code,
            subtotal: centsToDollars(res.amountRedeemedInCents),
            total: centsToDollars(res.amountRedeemedInCents),
            revenue: centsToDollars(res.amountRedeemedInCents),
            shipping: 0,
            tax: 0,
            discount: 0,
            currency: 'USD',
            is_gift_card: true,
            userId: user.user?.id,
            email: user.user.email,
            products: [
              {
                product_id: SEGMENT_EVENT_GIFT_CARD.product.id,
                sku: SEGMENT_EVENT_GIFT_CARD.product.sku,
                name: SEGMENT_EVENT_GIFT_CARD.product.name,
                price: centsToDollars(res.amountRedeemedInCents),
                quantity: 1,
              },
            ],
          });
        } catch (_err) {
          logger.warn({
            msg: 'SegmentRedeemGiftCardError',
            extra: {
              context: {
                code,
                userId: user.user.id,
              },
            },
          });
        }
      }
    } catch (err: unknown) {
      const error = err as AxiosError;
      //invalid code returns a 404 status code. So don't log that as an error.
      const statusResponse = error?.response?.status as number;
      if (statusResponse === 404) {
        setRedeemGiftCardError('Invalid gift card code');
        logger.info({
          msg: 'InvalidGiftCardCodeApplied',
          extra: {
            context: {
              code,
              userId: user.user?.id,
            },
          },
        });
      } else {
        setRedeemGiftCardError('An error occurred. Please try again later.');
        logger.error({
          msg: 'GiftCardRedemptionError',
          extra: {
            context: {
              code,
              userId: user.user?.id,
            },
          },
        });
      }
    }
  };
  return user.user?.id ? (
    <LayoutV2
      header={
        <Header
          leftContent={
            <>
              <Back onClickCallback={() => router.push('/dashboard')} />
              <Logo />
            </>
          }
          rightContent={<AccountMenu />}
        />
      }
      footer={<Footer isShowingCart={false} />}
    >
      <Grid
        container
        rowSpacing={2}
        sx={{
          margin: '2rem 0',
          display: 'flex',
          padding: '0 1rem',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Grid>
          <Typography variant="h3" sx={{ textAlign: 'center' }}>
            Redeem Gift Card
          </Typography>
        </Grid>
        <Grid>
          <Card sx={{ width: '20rem', marginTop: '2rem' }}>
            <CardContent
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h5" component="div">
                Current Balance:
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color:
                    redeemedCreditInCents && redeemedCreditInCents > 0
                      ? theme.palette.success.main
                      : theme.palette.text.secondary,
                }}
              >
                $
                {formatNumberWithCommas(
                  centsToDollars(creditBalanceInCents || 0),
                )}
              </Typography>
              {redeemedCreditInCents && (
                <Typography variant="caption">
                  This amount will be auto-applied at your next purchase
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <RedeemGiftCard
            redeemGiftCard={redeemGiftCardHandler}
            redeemGiftCardError={redeemGiftCardError}
            caption={'Enter your gift card code below'}
          />
          {redeemedCreditInCents && (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
              }}
            >
              $
              {centsToDollars(redeemedCreditInCents) +
                ' has been applied to your account’s balance.'}
            </Typography>
          )}
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" sx={{ margin: '2rem 0' }}>
            Frequently Asked Questions
          </Typography>
          <FaqAccordion faq={GIFT_CARD_FAQ} />
        </Grid>
      </Grid>
    </LayoutV2>
  ) : (
    <LayoutV2
      header={
        <Header
          leftContent={
            <>
              <Back onClickCallback={() => router.push('/dashboard')} />
              <Logo />
            </>
          }
          rightContent={<AccountMenu />}
        />
      }
      footer={<Footer isShowingCart={false} />}
    >
      <Grid
        container
        rowSpacing={2}
        sx={{
          margin: '2rem 0',
          display: 'flex',
          padding: '0 1rem',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <SignupAndLogin
          state="signup"
          title="Create an account or login to redeem your gift card"
        />
      </Grid>
    </LayoutV2>
  );
};

export default GiftCardRedeemComponent;
